import {
  AUTH_CLIENT_ID,
  AUTH_REDIRECT_URL,
  AUTH_URL,
  PATH_AFTER_LOGIN,
  getAuthAttemptContext,
  useGetToken,
  useSetAuthTokenEffect,
} from '@aibees/shared/auth';
import { useMount, usePostHog } from '@aibees/shared/hooks';
import { useRedirect, useSearchParams } from '@aibees/shared/router';
import { classnames } from '@aibees/shared/utils';
import { CircularProgress } from '@mui/material';
import { memo } from 'react';
import * as Styled from './styles';
import type { AuthEvent, SignInLoadingProps as Props } from './types';

export const SignInLoading = memo(({ className, code }: Props) => {
  const redirect = useRedirect();
  const { state } = useSearchParams();
  const { isError, mutateAsync: getTokenHandler, data } = useGetToken();
  const { track } = usePostHog<AuthEvent>();

  // CU-86e17r1cr: all `track(...)` calls in this component, the 200ms flush delay
  // before window.location.replace, and the .then handler are temporary diagnostic
  // instrumentation. Strip when investigation closes; keep the original
  // getTokenHandler / .catch / useSetAuthTokenEffect / redirect logic.
  useMount(() => {
    track('AUTH_SIGNIN_EFFECT_FIRED', {
      ...getAuthAttemptContext(),
      codePresent: !!code,
      codeLength: code?.length || 0,
      statePresent: !!state,
    });

    getTokenHandler({
      grant_type: 'authorization_code',
      code,
      redirect_uri: undefined, // Don't pass state - use base AUTH_REDIRECT_URL
    })
      .then((response) => {
        track('AUTH_SIGNIN_TOKEN_RESPONSE', {
          ...getAuthAttemptContext(),
          hasAccessToken: !!response?.access_token,
          hasRefreshToken: !!response?.refresh_token,
          hasIdToken: !!response?.id_token,
          tokenType: response?.token_type,
          expiresIn: response?.expires_in,
        });
      })
      .catch(async (e) => {
        const errorDescription = e?.response?.data?.error_description;
        track('AUTH_SIGNIN_TOKEN_ERROR', {
          ...getAuthAttemptContext(),
          errorMessage: e?.message,
          errorDescription,
          status: e?.response?.status,
          code: e?.code,
          hasResponse: !!e?.response,
        });

        if (errorDescription === 'Incorrect redirect_uri') {
          track('AUTH_SIGNIN_REDIRECT_URI_FALLBACK', getAuthAttemptContext());
          // Preserve state parameter when redirecting on error
          const encodedRedirectUri = encodeURIComponent(AUTH_REDIRECT_URL);
          const stateParam = state ? `&state=${encodeURIComponent(state)}` : '';
          // Brief delay so the redirect-fallback event has a chance to flush before navigation.
          await new Promise((r) => setTimeout(r, 200));
          window.location.replace(
            `${AUTH_URL}/auth?response_type=code&scope=openid&client_id=${AUTH_CLIENT_ID}&redirect_uri=${encodedRedirectUri}${stateParam}`
          );
        }
      });

    track('AUTH_SIGNIN_MUTATION_DISPATCHED', getAuthAttemptContext());
  });

  useSetAuthTokenEffect(data, () => {
    track('AUTH_SIGNIN_SET_TOKEN_EFFECT_FIRED', {
      ...getAuthAttemptContext(),
      hasState: !!state,
      stateLength: typeof state === 'string' ? state.length : 0,
    });
    // Use state as-is when present (already URL decoded by React Router).
    const target = !state ? PATH_AFTER_LOGIN : state;
    track('AUTH_SIGNIN_REDIRECT', {
      ...getAuthAttemptContext(),
      target: !state ? 'path_after_login' : 'state',
      targetLength: typeof target === 'string' ? target.length : 0,
    });
    redirect(target);
  });

  return (
    <Styled.Wrapper
      className={classnames('SignInLoading', className)}
      title="Sign In"
      description="we are trying to sign you in"
    >
      {isError ? (
        <Styled.Error>An error has happened</Styled.Error>
      ) : (
        <CircularProgress />
      )}
    </Styled.Wrapper>
  );
});

SignInLoading.displayName = 'SignInLoading';
